/**
 * MBA Lab — the analytics event registry.
 *
 * One frozen list of every event we fire and, for each, the params it is allowed to carry. This is
 * an ALLOW-list on purpose: a deny-list only blocks what someone already thought of, which is
 * exactly the blind spot that lets a salary figure or a raw search query reach a third party.
 * Anything not declared here is stripped by src/analytics-guard.js before it reaches gtag.
 *
 * Rules for editing:
 *  - Shipped event names are FROZEN. GA4 cannot merge a renamed event with its own history, so a
 *    rename silently splits a metric in two. Add new names; never repurpose old ones.
 *  - A param's MEANING may never change. If `result_count_bucket` needs different buckets, mint a
 *    v2 name and deprecate the original - silently rebucketing produces a chart that lies with no
 *    visible seam.
 *  - Never send a raw user-typed string, an exact money figure, or anything free-text. See
 *    docs/TOOLS.md and the guard's own denylist, which fails the build if you try.
 *  - Adding a param here is necessary but not sufficient: GA4 will collect it, but it is
 *    unreportable until it is also registered as a custom dimension in the GA4 admin UI.
 */

/**
 * Bumped when the meaning of the event stream changes in a way that would otherwise make a
 * year-over-year comparison lie. Rides on every event.
 */
export const SCHEMA_VERSION = 1;

/** Params any event may carry, without declaring them individually. */
export const COMMON_PARAMS = ['page_title', 'page_type', 'schema_version'];

/**
 * event name -> additional allowed param keys.
 * @type {Readonly<Record<string, ReadonlyArray<string>>>}
 */
export const EVENTS = Object.freeze({
  // Explicit page view. GA4 already sends one automatically on load, so this is reserved for
  // genuine virtual navigation - of which this site currently has none. See trackPageView.
  page_view: ['page_path'],

  // Emitted once by trackEvent when a page blows through MAX_EVENTS_PER_PAGE. Declared here so the
  // registry stays a complete list of everything this site can emit - it is the only name gtag is
  // called with directly, and an undeclared one would make a conformance scan read green while the
  // site emitted something the registry never mentioned.
  analytics_cap_reached: [],

  // --- Landing (src/home.js) ---
  landing_viewed: [],
  get_started_clicked: ['cta_placement'],
  // The try-the-parser demo. Never the pasted text - only that the demo was touched
  // (trigger: 'typed' | 'example') and, at most twice a page (first zero-row parse, first
  // non-empty parse), how it went: found_count_bucket (see parsedCountBucket) and whether
  // any found row carried a grade weight ('yes' | 'no').
  parser_demo_engaged: ['trigger'],
  parser_demo_result: ['found_count_bucket', 'weighted'],

  // --- Auth funnel (Views/Auth/SignIn.cshtml, and src/analytics-signal.js for the 302 paths) ---
  // sign_up and login are GA4 *recommended* names, so they get first-class treatment in its reports
  // rather than showing up as generic custom events. `method` is the recommended param.
  //
  // Report sign-ups by First user source/medium, never Session source/medium: the auth round trip
  // (land -> request code -> leave for email -> return) routinely exceeds GA4's 30-minute session
  // timeout, so the sign-up lands in an email session rather than the organic one that earned it.
  sign_up: ['method'],
  login: ['method'],
  invite_accepted: [],
  digest_preferences_saved: [],
  profile_updated: [],
  signin_email_submitted: [],
  signin_code_sent: [],
  signin_code_resent: [],
  signin_code_submitted: [],
  signin_failed: ['failure_reason'],
  signin_rate_limited: ['step'],

  // --- Directory search (wwwroot/js/college-search.js - outside webpack) ---
  // Note the param is search_LENGTH_bucket, not query_length_bucket: the guard's denylist blocks a
  // `query` token outright, and that is working as intended. No query text is ever sent, raw or
  // hashed - Search Console already gives us real queries, unhashed and uncapped, so a hash here
  // would add re-identification risk for data we can already get properly.
  directory_search: ['search_length_bucket', 'result_count_bucket', 'gap_kind'],
  search_result_selected: ['result_rank', 'result_count_bucket'],
  // The fetch failure that is today a silent console.warn. A broken search index is currently
  // invisible - the box just quietly stops finding anything.
  search_index_unavailable: [],
  state_filter_used: ['result_count_bucket'],
  state_filter_jump: [],

  // --- Outbound and internal conversion links (src/layout.js, delegated) ---
  // link_domain is declared here so the guard passes it, but must NOT be registered as a GA4 custom
  // dimension: 366 schools and growing would breach the ~500-value/day cardinality ceiling and
  // collapse the rest into an undrillable "(other)". It still lands in BigQuery's event_params,
  // which has no such limit. link_kind is the low-cardinality version for GA4 reporting.
  outbound_click: ['link_kind', 'link_domain'],
  cta_click: ['cta_id', 'cta_placement'],

  // --- Coming soon (Views/Home/ComingSoon.cshtml, inline - outside the bundle) ---
  coming_soon_viewed: [],
  notify_me_started: [],
  notify_me_completed: [],

  // --- Free tools (src/tools/lib/core.js composes `tool_<action>`) ---
  tool_open: ['tool_name'],
  tool_engage: ['tool_name', 'mode_id'],
  tool_result: [
    'tool_name',
    'mode_id',
    // Deliberately coarse across every tool: counts, enum ids and status strings only. Never the
    // underlying salary/tuition/debt figures, and never free-text notes.
    'tab',
    'day',
    'sections_set',
    'steps_started',
    'breakeven_status',
    'overlay_pattern',
    'scenario_id',
    'choice_id',
    'tree_id',
    'outcome',
    'not_started_count',
    'in_progress_count',
    'addressed_count',
    'real_mismatch_count',
    'some_friction_count',
    'good_fit_count',
  ],
  // tool_result carries 18 params of its own (21 with COMMON_PARAMS) - room enough on its own,
  // but not for the 11 more that wave 2 (four tools, 2026-08) needed on top: 18 + 11 + 3 = 32,
  // over GA4's 25-param ceiling. Rather than truncate any tool's signal or silently exceed the
  // limit, wave 2's new params live on this v2 name instead. Same shape and the same
  // trackToolAction('result', ...) call site pattern (see core.js) - a tool just passes
  // 'result_v2' as the action. When this one fills up too, mint a v3 rather than touching
  // tool_result or tool_result_v2's existing params, per this file's own rules above.
  tool_result_v2: [
    'tool_name',
    // Shared by the peer-evaluation drafter and the retrospective facilitator: how many of a
    // tool's fixed sections the user has actually started. Same meaning in both, hence one name.
    'sections_started',
    'evaluation_count',
    'commitment_count',
    'member_count',
    'has_overlap',
    // Bucketed with floor(), not round(): 12h30m reports '10-12'. Buckets are
    // '0-3' | '4-6' | '7-9' | '10-12' | '13+' and their meaning may never change - see the
    // rebucketing rule at the top of this file.
    'spread_hours_bucket',
    'school_count',
    'shortlist_count',
    'maybe_count',
    'ruled_out_count',
    'rated_count',
  ],
  tool_print: ['tool_name'],
  tool_download: ['tool_name'],
  tool_copy: ['tool_name'],
  tool_ai_review: ['tool_name'],
  tool_crosslink_click: ['tool_name', 'href'],
  // Fired by program-shortlist.js when /tools/data/schools-v1.json fails to load. Without it a
  // broken endpoint is indistinguishable from a drop in engagement - the page renders, it just
  // can never show a school. tool_name only, deliberately: no status code, no URL.
  tool_data_unavailable: ['tool_name'],
  // Pinning a tool to the hub rail (/hub/tools). tool_name only - which tools people keep is the
  // whole question, and nothing else about the act is worth collecting.
  tool_pin: ['tool_name'],
  tool_unpin: ['tool_name'],
});

/** @returns {boolean} whether `name` is a registered event. */
export function isRegisteredEvent(name) {
  return Object.prototype.hasOwnProperty.call(EVENTS, name);
}

/** @returns {string[]} every param key allowed on `name`, common ones included. */
export function allowedParamsFor(name) {
  if (!isRegisteredEvent(name)) return [];
  return COMMON_PARAMS.concat(EVENTS[name]);
}
